import { useTranslate } from '@tolgee/react';
import { DialogueRefObject, DialogueTtsLogisticsCardMessage } from 'components/Dialogue/types';
import { useChatContext } from '../../contexts/ChatContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { useInViewport } from 'ahooks';
import { MessageExtend } from '../MessageExtend';
import { MessageStatusDecorate } from '../MessageStatusDecorate';
import { Button, Divider, message, Popover, Skeleton, Space, Steps } from 'antd';
import { ItemImg } from 'components/ItemCard';
import Copy from 'views/Conversation/Sider/Copy';
import { TradeListContext } from 'views/Conversation/Sider/TradeContext';
import useTrade from 'views/Conversation/Sider/useTrade';
import { TradeContext } from 'views/Conversation/Sider/StateContext';
import styled from '@emotion/styled';
import Description from 'views/Conversation/Sider/Description';
import dayjs from 'dayjs';
import { LogisticsContent } from 'views/Conversation/Sider/TradeDetail';
import { useQuery } from '@tanstack/react-query';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import { ApiQueryKey } from 'services/config/queryKey.ts';
import { logisticsList, tradeList } from 'services/api/conversation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem.ts';
import { overseaTranslate } from 'services/api/overseaTranslate.ts';
import { getTtsMessageData } from 'services/api/conversation.convList.ts';
import { useIsSbsMode } from 'hooks/business/useIsSbs.ts';
import { isCancel } from 'axios';

interface Props {
  conversation: Conversation.Conversation;
  msg: DialogueTtsLogisticsCardMessage;
  raw: Conversation.Message;
}

export function TtsLogisticsCardMsgImpl({
  conversation,
  msg,
  dialogRef,
}: Props & {
  dialogRef?: DialogueRefObject | null;
}) {
  const { t } = useTranslate();

  const ref = useRef<HTMLDivElement>(null);

  const [inViewPort = false] = useInViewport(ref, {
    root: dialogRef?.dom,
  });

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const isSbs = useIsSbsMode();

  const { conversationId, buyerId, storeId, tunnelAccountId } = conversation;
  const {
    content: { orderId, packageId },
    status,
    raw: { msgId },
  } = msg;

  const useTradeResult = useTrade(storeId);

  const { list, setList } = useTradeResult;

  const { data } = useQuery({
    enabled: inViewPort || isFaq || isSbs,
    staleTime: 3 * 60 * 1000,
    queryKey: [ApiQueryKey.LogisticsCard, buyerId, storeId, tunnelAccountId, orderId, status],
    queryFn: ({ queryKey, signal }) => {
      if (status !== 'normal') {
        return;
      }
      return tradeList(
        {
          internal_buyer_id: queryKey[1],
          internal_store_id: queryKey[2],
          tunnelAccountId: queryKey[3],
          external_trade_ids: queryKey[4],
          page_size: 1,
          page_no: 1,
        },
        undefined,
        signal,
      )
        .then((res) => {
          const trade = res.tradeList?.[0];
          if (trade) {
            return logisticsList({
              internalStoreId: storeId,
              externalTradeIds: trade.externalTradeId,
              needPackageData: true,
            }).then((res) => {
              return {
                ...trade,
                logisticsList: res.data.data,
              };
            });
          } else {
            return Promise.reject(new Error(''));
          }
        })
        .catch((e) => {
          if (!isCancel(e)) {
            message.error(t('common.feedback.fetchFailed'));
          }
        });
    },
  });

  const { data: packages } = useQuery({
    queryKey: [ApiQueryKey.LogisticsCard, conversationId, msgId, status],
    enabled: inViewPort || isFaq || isSbs,
    staleTime: 3 * 60 * 1000,
    queryFn: () => {
      if (status !== 'normal') {
        return;
      }
      return getTtsMessageData({
        conversationId,
        msgId,
        isSbs,
      })
        .then(({ data }) => {
          return JSON.parse(data).packages as any[];
        })
        .catch(() => {
          message.error(t('common.feedback.fetchFailed'));
        });
    },
  });

  useEffect(() => {
    if (data && packages) {
      setList([
        {
          ...data,
          logisticsList: data.logisticsList.map((logistics) => {
            const targetPackage = packages.find((_) => _.package_id === logistics.packageNumber);
            if (!targetPackage) {
              return logistics;
            }
            return {
              ...logistics,
              shippingCarrier: targetPackage.shipping_provider_name,
              trackingInfo: targetPackage.tracking.map(
                ({ description, update_time_millis }: any) => {
                  return {
                    description,
                    updateTime: String((update_time_millis / 1000).toFixed(0)),
                    descriptionTranslation: {},
                  };
                },
              ),
            };
          }),
        },
      ]);
    }
  }, [data, packages, setList]);

  const trade = list?.[0];
  const logisticsPackage = trade?.logisticsList?.find(
    ({ packageNumber }) => packageNumber === packageId,
  );
  const lastTracking = logisticsPackage?.trackingInfo[0];
  const targetPackage = isSbs ? packages?.[0] : packages?.find((_) => _.package_id === packageId);

  const lang = useSystemLanguageValue();

  const [translated, setTranslated] = useState('');

  useEffect(() => {
    if (lastTracking?.description) {
      overseaTranslate(lastTracking.description, lang, 'side')
        .then((res) => {
          setTranslated(res);
        })
        .catch(() => {});
    }
  }, [lastTracking, lang]);

  const [visible, setVisible] = useState(false);

  return (
    <div ref={ref}>
      <TradeListContext.Provider
        value={{
          ...useTradeResult,
          conversation,
        }}
      >
        {trade && (isSbs || logisticsPackage) && targetPackage ? (
          <TradeContext.Provider
            value={{
              state: trade,
              setState: (tradeAction) =>
                setList([typeof tradeAction === 'function' ? tradeAction(trade) : tradeAction]),
            }}
          >
            <Container>
              <h4>{t('domain.order.logistics.summary')}</h4>
              <div>
                <ItemImg
                  hasSmallPic="both"
                  src={targetPackage.product_image}
                  alt="product"
                  target={dialogRef?.dom}
                />
                <section>
                  <p title={targetPackage.product_name}>{targetPackage.product_name}</p>
                  <div>
                    <span>
                      {t('scene.chatWorkspace.messageThread.logisticsCard.label.productCount', {
                        count: targetPackage.quantity,
                      })}
                      <Divider type="vertical" />
                      {targetPackage.paid_price}
                    </span>
                  </div>
                </section>
              </div>
              <dl>
                <dt>{targetPackage.shipping_provider_name}</dt>
                <dd>
                  {t('domain.order.logistics.field.trackingNumber')}：
                  {targetPackage.tracking_number}
                  <Copy
                    text={targetPackage.tracking_number}
                    successMessage={t('common.feedback.copySuccess')}
                    failedMessage={t('common.feedback.copyFailed')}
                  />
                </dd>
              </dl>
              <dl>
                <dt>
                  {t('scene.chatWorkspace.messageThread.logisticsCard.label.estimatedDelivery')}
                </dt>
                <dd>
                  {(() => {
                    const start = dayjs(Number(targetPackage.predict_delivery_time_min)).format(
                      'YYYY/MM/DD',
                    );
                    const end = dayjs(Number(targetPackage.predict_delivery_time_max)).format(
                      'YYYY/MM/DD',
                    );
                    if (start === end) {
                      return start;
                    }
                    return `${start} - ${end}`;
                  })()}
                </dd>
              </dl>
              {lastTracking && (
                <dl>
                  <dt>
                    {t('scene.chatWorkspace.messageThread.logisticsCard.label.latestLogistics')}
                  </dt>
                  <dd>
                    <Steps progressDot direction="vertical" current={0}>
                      <Steps.Step
                        key={lastTracking.updateTime || lastTracking.description}
                        title={<Description text={translated || lastTracking.description} />}
                        description={
                          Number(lastTracking.updateTime)
                            ? dayjs(Number(lastTracking.updateTime) * 1000).format(
                                'YYYY-MM-DD HH:mm',
                              )
                            : undefined
                        }
                      />
                    </Steps>
                  </dd>
                </dl>
              )}
              {isSbs || !logisticsPackage ? (
                <Button disabled type="text">
                  {t('common.action.viewDetails')}
                </Button>
              ) : (
                <Popover
                  visible={visible}
                  onVisibleChange={setVisible}
                  trigger="click"
                  arrowPointAtCenter
                  placement="right"
                  content={
                    <LogisticsContent
                      sendable={false}
                      reportTranslation={false}
                      platform="SHOPEE"
                      logisticsPackageList={trade.logisticsList || []}
                      showLogisticsPackageList={[logisticsPackage]}
                      onHide={() => setVisible(false)}
                    />
                  }
                >
                  <Button type="text">{t('common.action.viewDetails')}</Button>
                </Popover>
              )}
            </Container>
          </TradeContext.Provider>
        ) : (
          <Container>
            <h4>{t('domain.order.logistics.summary')}</h4>
            <div>
              <Skeleton.Image />
              <section>
                <Skeleton.Input active={inViewPort} size="small" />
                <Space>
                  <Skeleton.Button active={inViewPort} size="small" />
                  <Skeleton.Button active={inViewPort} size="small" />
                </Space>
              </section>
            </div>
            <dl>
              <Skeleton.Button active={inViewPort} size="small" />
              <Skeleton.Input active={inViewPort} size="small" />
            </dl>
            <dl>
              <Skeleton.Button active={inViewPort} size="small" />
              <Skeleton.Input active={inViewPort} size="small" />
            </dl>
            <dl>
              <Skeleton.Button active={inViewPort} size="small" />
              <Skeleton.Input active={inViewPort} size="small" />
            </dl>
            <dl>
              <Skeleton.Button active={inViewPort} size="small" />
            </dl>
          </Container>
        )}
      </TradeListContext.Provider>
    </div>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 360px;
  padding: 16px;
  background: var(--fett-chat-card-bg);
  border-radius: 4px;
  > h4 {
    margin-bottom: 12px;
    font-size: 16px;
  }
  > div {
    display: flex;
    height: 62px;
    > * {
      flex: 0 0 auto;
    }
    > section {
      flex: 1 0 0;
      margin-left: 8px;
      overflow: hidden;
      > p {
        width: 100%;
        margin-bottom: 2px;
        color: var(--fett-text-secondary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > div {
        display: flex;
        align-items: center;
        font: 12px;
        color: var(--fett-text-tertiary);
      }
    }
  }
  > dl {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    margin-top: 16px;
    > dt {
      font-weight: normal;
      color: var(--fett-text-tertiary);
    }
    > dd {
      margin-bottom: 0;
      .ant-steps-item-icon {
        width: 6px !important;
        height: 6px !important;
        margin-right: 4px !important;
        margin-top: 16px !important;
      }
      .ant-steps .ant-steps-item .ant-steps-item-title {
        color: var(--fett-text-secondary);
        font-size: 14px;
        line-height: 24px;
      }
      .ant-steps .ant-steps-item .ant-steps-item-description {
        padding-bottom: 0;
        color: var(--fett-text-tertiary);
      }
    }
  }
  > button {
    align-self: flex-end;
  }
  .ant-skeleton-image {
    width: 56px;
    height: 56px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
  .ant-skeleton-input {
    margin-bottom: 8px;
  }
  > dl .ant-skeleton-button {
    margin-bottom: 8px;
  }
  > .ant-skeleton {
    margin-top: 2px;
  }
`;

export default function TtsLogisticsCardMsg(props: Props) {
  const { raw, msg, conversation } = props;

  const { dialogRef } = useChatContext();

  return (
    <div>
      <MessageExtend
        raw={raw}
        msg={msg}
        conversation={conversation}
        content={
          <MessageStatusDecorate
            message={raw}
            status={msg?.status}
            content={<TtsLogisticsCardMsgImpl {...props} dialogRef={dialogRef} />}
            failCause={msg.raw.failCause}
            dialogRef={dialogRef || null}
          />
        }
      />
    </div>
  );
}
